import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Divider, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { fetchTrendArticles, fetchTrendStories } from './service';
import type { TrendArticle, TrendStory } from './types';
import TrendArticleCard from './components/TrendArticleCard';
import TrendArticleDetailDialog from './components/TrendArticleDetailDialog';
import TrendFilters from './components/TrendFilters';
import TrendStoryCard from './components/TrendStoryCard';
import TrendStoryDetailDialog from './components/TrendStoryDetailDialog';
import { extractTrendUiErrorMessage } from './errorUtils';
import { compareByRunPresentationThenDate, isLatestRunPresentation } from './runPresentationUtils';
import { matchesTrendChannel, useTrendChannel, TrendChannelFilter } from './channelFilter';
import TrendCardSkeletonGrid from './components/TrendCardSkeleton';
import { PAGE_HEADING_SX, SECTION_HEADING_SX, TREND_COLORS, getDesignSubCategoryLabel } from './trendTheme';

type TrendsSectionPageProps = {
  title: string;
  categories: string[];
};

function normalizeListKey(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function dedupeByKey<T>(items: T[], getKey: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getStorySemanticKey(story: TrendStory): string {
  const articleSignature = Array.isArray(story.articleIds) && story.articleIds.length > 0
    ? [...story.articleIds].map((entry) => normalizeListKey(entry)).sort().join('|')
    : '';
  if (articleSignature) return articleSignature;

  const sourceSignature = Array.isArray(story.sourceReferences) && story.sourceReferences.length > 0
    ? story.sourceReferences
      .map((entry) => normalizeListKey(entry.url || entry.title))
      .filter(Boolean)
      .sort()
      .slice(0, 3)
      .join('|')
    : '';
  if (sourceSignature) return sourceSignature;

  const titleSignature = normalizeListKey(story.title);
  const facetSignature = normalizeListKey(story.designSubCategory || story.storyType || story.category);

  return [facetSignature, titleSignature].filter(Boolean).join('|');
}

function getStoryListKey(story: TrendStory): string {
  return getStorySemanticKey(story) || story._id;
}

function storiesShareArticlesFrontend(left: TrendStory, right: TrendStory): boolean {
  const leftIds = Array.isArray(left.articleIds) ? left.articleIds : [];
  const rightIds = Array.isArray(right.articleIds) ? right.articleIds : [];
  if (leftIds.length === 0 || rightIds.length === 0) return false;
  const rightSet = new Set(rightIds);
  const overlapCount = leftIds.filter((id) => rightSet.has(id)).length;
  const smallerSize = Math.min(leftIds.length, rightIds.length);
  return overlapCount > 0 && overlapCount >= smallerSize * 0.5;
}

function dedupeStoriesByArticleOverlap(stories: TrendStory[]): TrendStory[] {
  const result: TrendStory[] = [];
  for (const story of stories) {
    if (result.some((existing) => storiesShareArticlesFrontend(existing, story))) continue;
    result.push(story);
  }
  return result;
}

function getArticleListKey(article: TrendArticle): string {
  const sourceSignature = Array.isArray(article.sourceReferences) && article.sourceReferences.length > 0
    ? article.sourceReferences
      .map((entry) => normalizeListKey(entry.url || entry.title))
      .filter(Boolean)
      .sort()
      .slice(0, 2)
      .join('|')
    : '';

  return article._id || [normalizeListKey(article.title), sourceSignature].filter(Boolean).join('|');
}

function getDesignPackagingBucket(story: TrendStory): 'design' | 'packaging' | null {
  if (
    story.designSubCategory === 'packaging_structure' ||
    story.category === 'packaging' ||
    story.storyType === 'packaging'
  ) {
    return 'packaging';
  }

  if (
    story.category === 'design' ||
    story.storyType === 'design'
  ) {
    return 'design';
  }

  return null;
}

export default function TrendsSectionPage({ title, categories }: TrendsSectionPageProps) {
  const { channel } = useTrendChannel();
  const [stories, setStories] = useState<TrendStory[]>([]);
  const [articles, setArticles] = useState<TrendArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [designSubCategory, setDesignSubCategory] = useState('');
  const [audience, setAudience] = useState('');
  const [behavior, setBehavior] = useState('');
  const [usageOccasion, setUsageOccasion] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [runFilter, setRunFilter] = useState<string>('all');
  const [selectedStory, setSelectedStory] = useState<TrendStory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<TrendArticle | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [storyResults, articleResults] = await Promise.allSettled([
          Promise.all(categories.map((entry) => fetchTrendStories({ category: entry }))),
          Promise.all(categories.map((entry) => fetchTrendArticles({ category: entry }))),
        ]);

        if (storyResults.status === 'fulfilled') {
          setStories(dedupeStoriesByArticleOverlap(dedupeByKey(storyResults.value.flat(), getStoryListKey)));
        } else {
          setStories([]);
        }

        if (articleResults.status === 'fulfilled') {
          setArticles(dedupeByKey(articleResults.value.flat(), getArticleListKey));
        } else {
          setArticles([]);
        }

        if (storyResults.status === 'rejected' && articleResults.status === 'rejected') {
          throw storyResults.reason;
        }
      } catch (err: any) {
        setError(extractTrendUiErrorMessage(err, 'Kunde inte ladda trendsektionen.'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [categories]);

  const isDesignPackagingView = categories.includes('design') || categories.includes('packaging');
  const availableCategories = useMemo(
    () => Array.from(new Set([...stories.map((story) => story.category), ...articles.map((article) => article.category)].filter(Boolean))),
    [stories, articles],
  );
  const availableDesignSubCategories = useMemo(
    () => Array.from(new Set(stories.map((story) => getDesignSubCategoryLabel(story.designSubCategory)).filter(Boolean))),
    [stories],
  );
  const availableAudiences = useMemo(
    () => Array.from(new Set(stories.flatMap((story) => story.audienceSegments).filter(Boolean))),
    [stories],
  );
  const availableBehaviors = useMemo(
    () => Array.from(new Set(stories.flatMap((story) => story.consumerBehaviors).filter(Boolean))),
    [stories],
  );
  const availableUsageOccasions = useMemo(
    () => Array.from(new Set(stories.flatMap((story) => story.usageOccasions).filter(Boolean))),
    [stories],
  );

  // A story counts as part of the latest run when any of its member articles is
  // flagged as latest-run, so the "Senaste körning" filter behaves consistently
  // for both stories and articles even though stories carry no runPresentation.
  const latestRunArticleIds = useMemo(() => new Set(
    articles
      .filter((article) => isLatestRunPresentation(article.runPresentation))
      .map((article) => String(article._id)),
  ), [articles]);

  const storyIsLatestRun = useCallback((story: TrendStory): boolean => (
    Array.isArray(story.articleIds)
    && story.articleIds.some((articleId) => latestRunArticleIds.has(String(articleId)))
  ), [latestRunArticleIds]);

  const filteredStories = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return stories.filter((story) => {
      const categoryMatch = !category || story.category === category;
      const designMatch = !designSubCategory || getDesignSubCategoryLabel(story.designSubCategory) === designSubCategory;
      const audienceMatch = !audience || story.audienceSegments.includes(audience);
      const behaviorMatch = !behavior || story.consumerBehaviors.includes(behavior);
      const occasionMatch = !usageOccasion || story.usageOccasions.includes(usageOccasion);
      const runMatch = runFilter !== 'latest' || storyIsLatestRun(story);
      const searchMatch = !searchLower || [
        story.title,
        story.summary,
        story.whatItMeansForUs,
        ...story.signalLabels,
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchLower);
      return categoryMatch && designMatch && audienceMatch && behaviorMatch && occasionMatch && runMatch && searchMatch;
    });
  }, [stories, search, category, designSubCategory, audience, behavior, usageOccasion, runFilter, storyIsLatestRun]);

  const filteredArticles = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const dateThreshold = dateFilter === '3d' ? Date.now() - 3 * 86400000
      : dateFilter === '7d' ? Date.now() - 7 * 86400000
      : dateFilter === '30d' ? Date.now() - 30 * 86400000
      : 0;
    return articles
      .filter((article) => {
        const categoryMatch = !category || article.category === category;
        const searchMatch = !searchLower || [article.title, article.excerpt, article.whyItMatters]
          .join(' ')
          .toLowerCase()
          .includes(searchLower);
        const dateMatch = !dateThreshold || (article.createdAt && new Date(article.createdAt).getTime() >= dateThreshold);
        const runMatch = runFilter !== 'latest' || isLatestRunPresentation(article.runPresentation);
        const channelMatch = matchesTrendChannel(article, channel);
        return categoryMatch && searchMatch && dateMatch && runMatch && channelMatch;
      })
      .sort((a, b) => (
        compareByRunPresentationThenDate(a, b)
        || (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
      ));
  }, [articles, search, category, dateFilter, runFilter, channel]);

  const filteredDesignStories = useMemo(
    () => filteredStories.filter((story) => getDesignPackagingBucket(story) === 'design'),
    [filteredStories],
  );
  const filteredPackagingStories = useMemo(
    () => filteredStories.filter((story) => getDesignPackagingBucket(story) === 'packaging'),
    [filteredStories],
  );
  return (
    <Box sx={{ bgcolor: TREND_COLORS.pageBg, minHeight: '100vh', mx: -3, mt: -3, px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack spacing={1.5}>
          <Typography sx={PAGE_HEADING_SX}>{title.toUpperCase()}</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip label={`${filteredArticles.length} artiklar`} size="small"
              sx={{ bgcolor: TREND_COLORS.navy, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
            <Chip label={`${filteredStories.length} berättelser`} size="small"
              sx={{ bgcolor: TREND_COLORS.olive, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
            <Box sx={{ ml: 'auto' }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TrendChannelFilter minWidth={150} sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }} />
                <TextField
                  select size="small" value={runFilter}
                  onChange={(e) => setRunFilter(e.target.value)}
                  sx={{ minWidth: 165, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                >
                  <MenuItem value="all">Alla körningar</MenuItem>
                  <MenuItem value="latest">Senaste körning</MenuItem>
                </TextField>
                <TextField
                  select size="small" value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  sx={{ minWidth: 160, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                >
                  <MenuItem value="all">Alla datum</MenuItem>
                  <MenuItem value="3d">Senaste 3 dagarna</MenuItem>
                  <MenuItem value="7d">Senaste 7 dagarna</MenuItem>
                  <MenuItem value="30d">Senaste 30 dagarna</MenuItem>
                </TextField>
              </Stack>
            </Box>
          </Stack>
        </Stack>

        <TrendFilters
          search={search}
          category={category}
          categories={availableCategories}
          designSubCategory={designSubCategory}
          designSubCategories={availableDesignSubCategories}
          audience={audience}
          audiences={availableAudiences}
          behavior={behavior}
          behaviors={availableBehaviors}
          usageOccasion={usageOccasion}
          usageOccasions={availableUsageOccasions}
          onSearchChange={setSearch}
          onCategoryChange={setCategory}
          onDesignSubCategoryChange={setDesignSubCategory}
          onAudienceChange={setAudience}
          onBehaviorChange={setBehavior}
          onUsageOccasionChange={setUsageOccasion}
        />

        {loading && <TrendCardSkeletonGrid count={6} />}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && isDesignPackagingView && (
          <Stack spacing={4}>
            {filteredDesignStories.length > 0 && (
              <Box>
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>DESIGNTRENDER</Typography>
                <Grid container spacing={3}>
                  {filteredDesignStories.map((story) => (
                    <Grid key={story._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                      <TrendStoryCard story={story} onOpen={(entry) => setSelectedStory(entry)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredPackagingStories.length > 0 && (
              <Box>
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>FÖRPACKNINGSTRENDER</Typography>
                <Grid container spacing={3}>
                  {filteredPackagingStories.map((story) => (
                    <Grid key={story._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                      <TrendStoryCard story={story} onOpen={(entry) => setSelectedStory(entry)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredArticles.length > 0 && (
              <Box>
                {(filteredDesignStories.length > 0 || filteredPackagingStories.length > 0) && (
                  <Divider sx={{ borderColor: TREND_COLORS.divider, mb: 3 }} />
                )}
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>ARTIKLAR</Typography>
                <Grid container spacing={3}>
                  {filteredArticles.map((article) => (
                    <Grid key={article._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                      <TrendArticleCard article={article} onReadMore={(entry) => setSelectedArticle(entry)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredDesignStories.length === 0 && filteredPackagingStories.length === 0 && filteredArticles.length === 0 && (
              <Alert severity="info">Inga stories eller artiklar matchar nuvarande filter.</Alert>
            )}
          </Stack>
        )}

        {!loading && !error && !isDesignPackagingView && (
          <Stack spacing={4}>
            {filteredStories.length > 0 && (
              <Box>
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>TEMAN</Typography>
                <Grid container spacing={3}>
                  {filteredStories.map((story) => (
                    <Grid key={story._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                      <TrendStoryCard story={story} onOpen={(entry) => setSelectedStory(entry)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredArticles.length > 0 && (
              <Box>
                {filteredStories.length > 0 && (
                  <Divider sx={{ borderColor: TREND_COLORS.divider, mb: 3 }} />
                )}
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>ARTIKLAR</Typography>
                <Grid container spacing={3}>
                  {filteredArticles.map((article) => (
                    <Grid key={article._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                      <TrendArticleCard article={article} onReadMore={(entry) => setSelectedArticle(entry)} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredStories.length === 0 && filteredArticles.length === 0 && (
              <Alert severity="info">Inga stories eller artiklar matchar de valda filtren ännu.</Alert>
            )}
          </Stack>
        )}

        <TrendStoryDetailDialog
          storyId={selectedStory?._id || null}
          open={!!selectedStory}
          onClose={() => setSelectedStory(null)}
        />

        <TrendArticleDetailDialog
          articleId={selectedArticle?._id || null}
          open={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
        />
      </Stack>
    </Box>
  );
}
